import { Popover as NextUIPopover } from '@nextui-org/react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchInput from '../Input/SearchInput';
import Radio from '../Radio';
import {
  Actions,
  CancelButton,
  Chevron,
  ConfirmButton,
  ContentContainer,
  DropdownLabel,
  DropdownWrapper,
  EmptyState,
  ItemButton,
  ItemDescription,
  ItemLeftContent,
  ItemRightContent,
  ItemsContainer,
  ItemTitle,
  SearchContainer,
  TriggerButton,
  TriggerChevronContainer,
  TriggerContent,
  TriggerLabel,
} from './ActionsDropdown.styles';

export interface ActionsDropdownItem {
  label: string;
  value: any;
  description?: string;
}

export enum ActionsDropdownLabelPosition {
  TOP = 'top',
  LEFT = 'left',
}

interface ActionsDropdownProps {
  label?: string;
  items: ActionsDropdownItem[];
  selectedValue?: string;
  setSelectedValue: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  cancelText?: string;
  confirmText?: string;
  disabled?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  disabledConfirm?: boolean;
  emptyStateText?: string;
  labelPosition?: ActionsDropdownLabelPosition;
}

const ActionsDropdown = ({
  label,
  items,
  selectedValue,
  setSelectedValue,
  placeholder,
  searchPlaceholder,
  cancelText,
  confirmText,
  emptyStateText,
  disabled,
  onCancel,
  onConfirm,
  disabledConfirm,
  labelPosition = ActionsDropdownLabelPosition.TOP,
}: ActionsDropdownProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const closingViaConfirmRef = useRef(false);
  const suppressCancelFromNextOpenChangeRef = useRef(false);

  const selectedItem = useMemo(
    () => items.find((item) => String(item.value) === selectedValue) ?? null,
    [selectedValue, items],
  );

  const filteredItems = useMemo(
    () => items.filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase().trim())),
    [items, searchText],
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      closingViaConfirmRef.current = false;
      suppressCancelFromNextOpenChangeRef.current = false;
      return;
    }
    setSearchText('');
    if (suppressCancelFromNextOpenChangeRef.current) {
      suppressCancelFromNextOpenChangeRef.current = false;
      return;
    }
    if (closingViaConfirmRef.current) {
      closingViaConfirmRef.current = false;
    } else {
      onCancel?.();
    }
  };

  const handleCancel = () => {
    suppressCancelFromNextOpenChangeRef.current = true;
    setSearchText('');
    setIsOpen(false);
    onCancel?.();
  };

  const handleConfirm = () => {
    closingViaConfirmRef.current = true;
    setSearchText('');
    setIsOpen(false);
    onConfirm?.();
  };

  return (
    <DropdownWrapper $labelPosition={labelPosition}>
      {label && <DropdownLabel>{label}</DropdownLabel>}
      <NextUIPopover isOpen={isOpen} onOpenChange={handleOpenChange} placement="bottom-left">
        <NextUIPopover.Trigger>
          <TriggerButton disabled={disabled} $isOpen={isOpen} auto>
            <TriggerContent>
              <TriggerLabel>{selectedItem?.label ?? placeholder ?? t('common.select')}</TriggerLabel>
              <TriggerChevronContainer>
                <Chevron $isOpen={isOpen} />
              </TriggerChevronContainer>
            </TriggerContent>
          </TriggerButton>
        </NextUIPopover.Trigger>
        <ContentContainer>
          <SearchContainer>
            <SearchInput
              onTextChange={setSearchText}
              searchTextLocal={searchText}
              placeholder={searchPlaceholder || t('common.search')}
              desktopWidth="100%"
            />
          </SearchContainer>
          <ItemsContainer>
            <Radio.Group value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)}>
              {filteredItems.length ? (
                filteredItems.map((item) => (
                  <ItemButton
                    key={item.value}
                    type="button"
                    $isSelected={selectedValue === String(item.value)}
                    onClick={() => setSelectedValue(String(item.value))}
                  >
                    <ItemLeftContent>
                      <Radio
                        isSelected={selectedValue === String(item.value)}
                        value={String(item.value)}
                        aria-label={`actionsDropdownRadio_${String(item.value)}`}
                      />
                      <ItemTitle>{item.label}</ItemTitle>
                    </ItemLeftContent>
                    <ItemRightContent>
                      {item.description && <ItemDescription>{item.description}</ItemDescription>}
                    </ItemRightContent>
                  </ItemButton>
                ))
              ) : (
                <EmptyState>{emptyStateText || t('common.noResults')}</EmptyState>
              )}
            </Radio.Group>
          </ItemsContainer>
          <Actions>
            <CancelButton onPress={handleCancel}>{cancelText || t('common.cancel')}</CancelButton>
            <ConfirmButton onPress={handleConfirm} disabled={disabledConfirm}>
              {confirmText || t('common.save')}
            </ConfirmButton>
          </Actions>
        </ContentContainer>
      </NextUIPopover>
    </DropdownWrapper>
  );
};

export default ActionsDropdown;
